'use client';

import { FC, useContext } from 'react';
import { clsx } from 'clsx';
import Link from 'next/link';
import usePagination from '@/hooks/usePagination';
import NextPagination from '@/components/Icon/NextPagination';
import { createUrl, createQueryString } from '@/utils/routerUtils';
import AlgonomyContext, { AlgonomyContextState } from '@/context/SearchResultsContext';
import { PaginationProps } from './types';

import styles from './Pagination.module.css';


const Pagination: FC<PaginationProps> = ({ numFound, pageNumber, pageSize, pathName, searchParams, pgName }) => {
  const { mobile } = useContext(AlgonomyContext) as AlgonomyContextState;
  const pagination = usePagination(numFound, pageSize, pageNumber, 1, mobile);
  const prevPaginationUrl = createUrl(
      pathName,
      createQueryString({ ...searchParams, [pgName]: String(pageNumber - 1) } as Record<string, string | string[]>),
    );
  const nextPaginationUrl = createUrl(
    pathName,
    createQueryString({ ...searchParams, [pgName]: String(pageNumber <= 1 ? 2 : pageNumber + 1) } as Record<
      string,
      string | string[]
    >),
  );
  return (
    <nav className={styles.pagination}>
      <ul className={styles.paginationNavigation}>
        {pageNumber !== 0 && pageNumber !== 1 && (
          <li className={styles.paginationItem}>
            <Link data-testid={'pagination-prev-btn'} title={'prev page'} className={`${styles.prevButton}`} href={prevPaginationUrl} prefetch={false}>
              <NextPagination />
            </Link>
          </li>
        )}
        {pagination?.map((paginationItem: number, idx: number) => (
          <li
            data-testid={`pagination-item-${idx}-${paginationItem}`}
            key={`${idx}-${paginationItem}`}
            className={clsx(styles.paginationItem, {
              [styles.paginationItemActive]:
                paginationItem === pageNumber || (pageNumber === 0 && paginationItem === 1),
              [styles.paginationItemDot]: paginationItem === -1,
            })}
          >
              {paginationItem !== -1 ? (
                <Link
                  href={`${createUrl(pathName, createQueryString({ ...searchParams, [pgName]: String(paginationItem) } as Record<string, string | string[]>))}`}
                  prefetch={false}
                >
                  {paginationItem}
                </Link>
              ) : (
                '...'
              )}
          </li>
        ))}
        {pagination[pagination.length - 1] !== pageNumber && pagination.length > 1 && (
          <li className={styles.paginationItem}>
            <Link data-testid={'pagination-next-btn'} title={'next page'} className={`${styles.nextButton}`} href={nextPaginationUrl} prefetch={false}>
              <NextPagination />
            </Link>
          </li>
        )}
      </ul>
    </nav>
  );
};

export default Pagination;
